import { useState } from 'react'

import { Box, Button, Spinner } from '@factorialco/deprecated-design-system'
import isNil from 'lodash/isNil'
import xor from 'lodash/xor'

import Callout from 'modules/core/components/Callout'
import EmployeeSelector from 'modules/core/components/EmployeeSelector'
import Modal from 'modules/core/components/Modal'
import { useUnsafeCallback } from 'modules/core/hooks/useCallback'
import { employeeLegalEntity } from 'modules/core/lib/employeeLegalEntity'
import i18n from 'modules/core/lib/i18n'

import type { ContractVersions } from 'modules/contracts/store/collections/contractVersions'
import type Employee from 'modules/employees/store/models/Employee'
import { useBulkAssignEmployeesToPolicy } from 'modules/payroll_bundle/hooks/useBulkAssignEmployeesToPolicy'
import { usePolicyAssignmentEligibleEmployees } from 'modules/payroll_bundle/hooks/usePolicyAssignmentEligibleEmployees'
import type Policy from 'modules/payroll_bundle/store/models/Policy'

interface Props {
  policyActiveEmployeeIds: number[]
  employees: Employee[]
  policy: Policy
  onClose: () => void
  onCompleted: (args: { employeeIds: number[]; policyId: number }) => void
  contractVersions: ContractVersions
}

const AssignEmployeesToPolicyModal = ({
  policy,
  policyActiveEmployeeIds,
  employees,
  onClose,
  onCompleted,
  contractVersions,
}: Props) => {
  const [selectedEmployees, setSelectedEmployees] = useState<number[]>([])
  const { fetching: fetchingBulkAssignEmployeesToPolicy, update } = useBulkAssignEmployeesToPolicy()
  // We show on the modal only the employees with an active contract that are not already assigned to the policy.
  const eligibleEmployees = employees.filter((employee) => {
    const isActiveOnPolicy =
      !policyActiveEmployeeIds.includes(employee.id) && !employee.isTerminated

    const legalEntity = employeeLegalEntity(employee)
    const matchesCurrencyAndCountry =
      legalEntity.attributes.currency === policy.attributes.currency &&
      legalEntity.attributes.country === policy.attributes.country

    if (policy.attributes.currency !== null && policy.attributes.country !== null) {
      return isActiveOnPolicy && matchesCurrencyAndCountry
    } else {
      return isActiveOnPolicy
    }
  })

  const { fetching, policyAssignmentEligibleEmployeeIds } = usePolicyAssignmentEligibleEmployees(
    eligibleEmployees.map((employee) => employee.id)
  )
  // Employees with historical data are not eligible to be assigned to a policy. In order to know more about how we
  // determine if an employee has historical data, you can check the repository PayrollPolicyAssignmentEligibleEmployees.
  const ineligibleEmployees = policyAssignmentEligibleEmployeeIds
    ? eligibleEmployees.filter(
        (employee) => !policyAssignmentEligibleEmployeeIds.includes(employee.id)
      )
    : eligibleEmployees

  const handleSelectEmployee = useUnsafeCallback(
    (employeeId?: number | null) => {
      if (isNil(employeeId)) {
        setSelectedEmployees([])

        return
      }

      setSelectedEmployees(xor(selectedEmployees, [employeeId]))
    },
    [selectedEmployees, setSelectedEmployees]
  )

  async function handleAssignEmployeesSubmit() {
    const policyId = Number(policy.id)

    await update({
      payrollPolicyId: policyId,
      employeeIds: selectedEmployees,
    })

    onCompleted({ employeeIds: selectedEmployees, policyId })
  }

  return (
    <Modal
      title={i18n.t(
        'app_store_v2.apps.payroll_bundle.settings.payroll_policy.assign_to_policy_modal.title',
        { policyName: policy.attributes.name }
      )}
      description={i18n.t(
        'app_store_v2.apps.payroll_bundle.settings.payroll_policy.assign_to_policy_modal.description_self_onboarding'
      )}
      illustration="add-user"
      footer={
        <>
          <Button type="secondary" onClick={onClose}>
            {i18n.t(
              'app_store_v2.apps.payroll_bundle.settings.payroll_policy.assign_to_policy_modal.cancel_label'
            )}
          </Button>

          <Button
            disabled={fetchingBulkAssignEmployeesToPolicy}
            onClick={handleAssignEmployeesSubmit}
          >
            {i18n.t(
              'app_store_v2.apps.payroll_bundle.settings.payroll_policy.assign_to_policy_modal.submit_labeL_self_onboarding'
            )}
          </Button>
        </>
      }
      onClose={onClose}
    >
      <Box gap="s24">
        {fetching && (
          <Box alignItems="center" aria-label={i18n.t('common.loading')}>
            <Spinner />
          </Box>
        )}
        {!fetching && (
          <>
            <EmployeeSelector
              employees={eligibleEmployees}
              selectedEmployeeIds={selectedEmployees}
              onSelectEmployee={handleSelectEmployee}
              onSelectMultiple={setSelectedEmployees}
              disabledEmployeeIds={ineligibleEmployees.map((employee) => employee.id)}
              contractVersions={contractVersions}
            />

            {policy.attributes.currency !== null && policy.attributes.country !== null && (
              <Callout
                title={i18n.t(
                  'payroll_bundle.onboarding.payroll_policy_step.employees_selector_title'
                )}
                type="info"
                description={i18n.t(
                  'payroll_bundle.onboarding.payroll_policy_step.form_tips.employees'
                )}
              />
            )}
          </>
        )}
      </Box>
    </Modal>
  )
}

export default AssignEmployeesToPolicyModal
